/* CompanyRoutesSection — company-first official routes for the selected state + city.
   Data: GET /api/blue-collar/company-routes/{stateCode}?city=…
   Read-only content inside the Blue-Collar section; never touches the lock funnel. */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Wrench, Car, Factory, ShieldAlert, Info, MapPin } from 'lucide-react';
import { CompanyRouteCard } from './CompanyRouteCard';
import { EmployerRouteCard } from './EmployerRouteCard';
import { OfficialLink, CoverImg } from './shared';

const API = process.env.REACT_APP_BACKEND_URL;

function SectionHead({ icon: Icon, title, sub, testId, cover }) {
  return (
    <div className="mb-3 flex items-start gap-3" data-testid={testId}>
      {cover?.url && <CoverImg src={cover.url} alt={cover.alt} className="w-24 sm:w-32 shrink-0 rounded-xl overflow-hidden" testId={`${testId}-cover`} />}
      <div className="min-w-0">
        <h2 className="text-base md:text-lg font-extrabold text-gray-900 flex items-center gap-2">
          <span className="w-7 h-7 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center shrink-0"><Icon size={14} /></span>
          {title}
        </h2>
        {sub && <p className="text-xs text-gray-500 mt-1 max-w-2xl">{sub}</p>}
      </div>
    </div>
  );
}

export function CompanyRoutesSection({ stateCode, city }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!stateCode || !city) return;
    setData(null); setErr('');
    axios.get(`${API}/api/blue-collar/company-routes/${stateCode}`, { params: { city } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Could not load company routes.'));
  }, [stateCode, city]);

  if (!stateCode || !city) return null;
  if (err) return <div className="max-w-2xl mx-auto mt-8 bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-xl px-4 py-2" data-testid="bc-routes-error">{String(err)}</div>;
  if (!data) return <div className="mt-10 text-center" data-testid="bc-routes-loading"><Loader2 className="w-6 h-6 animate-spin text-amber-600 mx-auto" /></div>;

  const noLocal = data.skillFirstCards.length === 0 && data.vehicleCards.length === 0;

  return (
    <div className="mt-10 space-y-10" data-testid="bc-company-routes">
      {noLocal ? (
        <div className="max-w-2xl mx-auto text-center rounded-3xl border-2 border-dashed border-amber-200 bg-white/70 p-8" data-testid="bc-routes-empty">
          <MapPin size={26} className="mx-auto text-amber-400 mb-2" />
          <p className="text-sm font-bold text-gray-800">No verified company route is recorded for {city} yet.</p>
          <p className="text-xs text-gray-600 mt-2 leading-relaxed">{data.noEvidencePolicy}</p>
        </div>
      ) : (
        <>
          {data.skillFirstCards.length > 0 && (
            <section>
              <SectionHead icon={Wrench} title={`Skill-first & home-service work in ${city}`}
                sub="Company routes where your skill matters more than owning a vehicle. Tap a card to explore work roles, then confirm city and category in the official flow."
                testId="bc-skill-first-heading" cover={data.sectionCovers?.cityCompanyCards} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5 items-start" data-testid="bc-skill-first-grid">
                {data.skillFirstCards.map((c) => <CompanyRouteCard key={c.cityEvidenceId} c={c} />)}
              </div>
            </section>
          )}
          {data.vehicleCards.length > 0 && (
            <section>
              <SectionHead icon={Car} title={`Vehicle or asset-confirmation routes in ${city}`}
                sub="Shown separately: these routes usually need a vehicle, licence, permits, a joining/onboarding fee or running costs. Confirm the vehicle, fee and cost fields before thinking about income."
                testId="bc-vehicle-heading" />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5 items-start" data-testid="bc-vehicle-grid">
                {data.vehicleCards.map((c) => <CompanyRouteCard key={c.cityEvidenceId} c={c} />)}
              </div>
            </section>
          )}
        </>
      )}

      {data.employerHiringCards.length > 0 && (
        <section>
          <SectionHead icon={Factory} title="Official Manufacturing & Industrial Employer Routes"
            sub={`Official employer career routes with facility evidence in ${data.stateName}. Separate from marketplace, delivery or driver work. Check the verified locality — it may differ from your city.`}
            testId="bc-manufacturing-heading" cover={data.sectionCovers?.officialManufacturingEmployers} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5 items-start" data-testid="bc-manufacturing-grid">
            {data.employerHiringCards.map((e) => <EmployerRouteCard key={e.employerId} e={e} />)}
          </div>
        </section>
      )}

      {data.employerRouteOnlyCards?.length > 0 && (
        <section>
          <SectionHead icon={Factory} title="Check official route — employer routes without strict facility proof"
            sub="These employers publish an official careers route, but the exact facility/locality pair did not meet the strict verification pass. They are not verified openings. Check location and role on the official portal."
            testId="bc-route-only-heading" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5 items-start" data-testid="bc-route-only-grid">
            {data.employerRouteOnlyCards.map((e) => <EmployerRouteCard key={e.employerId} e={e} />)}
          </div>
        </section>
      )}

      {data.nationalRoutes.length > 0 && (
        <details className="max-w-3xl mx-auto rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm" data-testid="bc-national-routes">
          <summary className="cursor-pointer font-bold text-gray-800 text-xs uppercase tracking-widest flex items-center gap-2">
            <Info size={14} /> National official routes — not a local availability claim
          </summary>
          <p className="text-xs text-gray-500 mt-2 leading-relaxed">These companies publish a national worker route. Listing here does not mean they are onboarding in {city}; confirm your city inside the official flow.</p>
          <ul className="mt-3 grid sm:grid-cols-2 gap-2">
            {data.nationalRoutes.map((n) => (
              <li key={n.companyId} className="flex items-center justify-between gap-2 rounded-xl border border-gray-100 px-3 py-2">
                <span className="text-xs font-bold text-gray-800">{n.companyName}</span>
                <OfficialLink href={n.officialRouteUrl} label="Official route" tone="outline" small testId={`bc-national-${n.companyId}`} />
              </li>
            ))}
          </ul>
        </details>
      )}

      <section className="max-w-3xl mx-auto rounded-3xl border-2 border-rose-100 bg-white p-5 md:p-6" data-testid="bc-safety-block">
        <h2 className="text-base font-extrabold text-gray-900 flex items-center gap-2"><ShieldAlert size={16} className="text-rose-600" /> Costs, safety and scam checks</h2>
        <p className="text-sm text-gray-800 mt-2 leading-relaxed">{data.incomeDisclaimer}</p>
        <p className="text-[10px] font-extrabold uppercase tracking-widest text-gray-500 mt-4 mb-1.5">Costs to confirm before earnings</p>
        <div className="flex flex-wrap gap-1.5">
          {data.costFields.map((f) => <span key={f} className="text-[11px] font-semibold bg-gray-50 text-gray-700 border border-gray-200 px-2 py-0.5 rounded-full">{f}</span>)}
        </div>
        <p className="text-xs text-rose-800 font-semibold mt-4 leading-relaxed">{data.antiFraudRule}</p>
        <p className="text-[11px] text-gray-500 mt-3 leading-relaxed">{data.youthNote}</p>
        <p className="text-[10px] text-gray-400 mt-2">Company-first catalogue checked {data.checkedAt}.</p>
      </section>
    </div>
  );
}
